import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import DropdownNav from './DropdownNav';
import CloseIcon from '../icons/CloseIcon';
import LocationIcon from '../icons/LocationIcon';
import HamburgerMenuIcon from '../icons/HamburgerMenuIcon';
import LogoIcon from '../icons/LogoIcon';
import { contextState } from '../../utils/ContextProvider';
import useWindowSize from '../../utils/useWindowSize';
import styles from './HeaderInformational.module.css';
import a11yStyles from '../../styling/a11y.module.css';

/**
 *
 * @param {object} props
 * @param {string=} props.className
 * @param {string} props.path
 * @param {boolean} props.withHero
 */
function HeaderInformational({
  className: headerClassName = '',
  path,
  withHero,
}) {
  const { contextData } = contextState();
  const [showOpaqueHeader, setShowOpaqueHeader] = useState(!withHero);

  const [showNavMobile, setShowNavMobile] = useState(false);
  const [currentPage, setCurrentPage] = useState('');
  const windowSize = useWindowSize();

  useEffect(() => {
    if (windowSize && windowSize.width > 1000) {
      document.body.classList.remove('noscroll');
      setShowNavMobile(false); // Get styles back to normal for header nav
    }
    // For when mobile nav was open when returning from desktop <-> mobile
    if (windowSize && windowSize.width <= 1000 && showNavMobile) {
      document.body.classList.add('noscroll');
    }
  }, [showNavMobile, windowSize]);

  useEffect(() => {
    if (showNavMobile) {
      document.body.classList.add('noscroll');
    } else {
      document.body.classList.remove('noscroll');
    }
  }, [showNavMobile]);

  useEffect(() => {
    setShowOpaqueHeader(
      !withHero || showNavMobile || contextData.showOpaqueHeader,
    );
  }, [contextData, showNavMobile]);

  useEffect(() => {
    setCurrentPage(path);
  }, [path]);

  return (
    <header
      className={`${
        withHero ? styles.opaqueFixed : styles.opaqueSticky
      } ${headerClassName}`}
    >
      <a className={a11yStyles.hidden} href="#main-content">
        Skip to Content
      </a>
      <div
        className={`${styles.navWrapper} ${
          showOpaqueHeader ? styles.opaqueHeader : ''
        }`}
        id="navWrapper"
      >
        <div className={styles.navRow}>
          <div className={`${styles.headerLink} ${styles.locationList}`}>
            <Link
              className={`${styles.headerNavButton} ${styles.locationLink}`}
              to="/locations-map/"
            >
              <span>Find a Fridge</span>
              <LocationIcon />
            </Link>
          </div>
          <Link aria-label="Farmer's Fridge" className={styles.logo} to="/">
            <LogoIcon />
          </Link>
          <div className={styles.navLinks}>
            <button
              className={styles.mobileNavButton}
              type="button"
              aria-label={showNavMobile ? 'Hide Menu' : 'Show Menu'}
              onClick={() => setShowNavMobile(!showNavMobile)}
            >
              {!showNavMobile && (
                <HamburgerMenuIcon className={styles.navIcon} />
              )}
              {showNavMobile && <CloseIcon className={styles.navIcon} />}
            </button>
            <nav
              aria-label="Primary Links"
              className={`${styles.navLists} ${
                showNavMobile ? styles.open : ''
              }`}
            >
              <ul className={styles.navList}>
                <li
                  key="menu"
                  className={
                    currentPage.includes('/menu')
                      ? styles.headerLinkActive
                      : styles.headerLink
                  }
                >
                  <Link className={styles.headerNavButton} to="/menu">
                    Menu
                  </Link>
                </li>
                <li
                  className={
                    currentPage.includes('/locations-map')
                      ? styles.headerLinkActive
                      : styles.headerLink
                  }
                >
                  <Link className={styles.headerNavButton} to="/locations-map">
                    Locations
                  </Link>
                </li>
                <DropdownNav
                  buttonClass={styles.headerNavButton}
                  currentPage={currentPage}
                  navItemActiveClass={styles.headerLinkActive}
                  navItemClass={styles.headerLink}
                  navItems={[
                    {
                      label: 'Our Story',
                      type: 'text-link',
                      url: '/about-us',
                    },
                    {
                      label: 'Our App',
                      type: 'text-link',
                      url: '/app',
                    },
                    {
                      label: 'Careers',
                      type: 'text-link',
                      url: '/careers',
                    },
                  ]}
                  title="About Us"
                />
                <li
                  key="for-get-a-fridge"
                  className={
                    currentPage.includes('/get-a-fridge')
                      ? styles.headerLinkActive
                      : styles.headerLink
                  }
                >
                  <Link className={styles.headerNavButton} to="/get-a-fridge">
                    Get a Fridge
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
}

export default HeaderInformational;
